import { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import DrawerHeader from "../../components/common/DrawerHeader";
import FormNavBar from "../../components/FormHeader/FormNavBar";
import FieldHeader from "../../components/KycForm/FieldHeader";
import GridItem from "../../components/KycForm/GridItem";
import SelectFieldComponent from "../../components/common/SelectFieldComponent";
import IndividualForm from "../../components/IndividualForm/IndividualForm";
import CorporateForm from "../../components/CorporateForm/CorporateForm";
import {
	ACCOUNTTYPES,
	CONSTANTSSTRINGS,
	stateIdMap,
	emailRegex,
} from "../../utils/constants";
import {
	getCountry,
	getState,
	getCity,
	getDocUrl,
	kycSubmission,
} from "../../services/clientForm";

const selectFieldWidth = "16.75rem";

const defaultIndividualFields = {
	title: "",
	firstname: "",
	middlename: "",
	lastname: "",
	gender: "",
	dateOfBirth: null,
	email: "",
	phone: "",
	secondaryPhone: "",
	chn: "",
	previousAddress: "",
	addressOne: "",
	addressTwo: "",
	country: "Nigeria",
	state: "",
	city: "",
	nationality: "",
	identityType: "",
	identityNumber: "",
	identityExpiryDate: null,
	occupation: "",
	employer: "",
	employerAddress: "",
	enterIdentityType: "",
	passportFile: "",
	passportUrl: "",
	signatureFile: "",
	signatureUrl: "",
	identificationFile: "",
	identificationUrl: "",
	otherDocumentsFile: "",
	otherDocumentsUrl: "",
};

const defaultCorporateFields = {
	companyName: "",
	email: "",
	phone: "",
	secondaryPhone: "",
	chn: "",
	previousAddress: "",
	addressOne: "",
	addressTwo: "",
	country: "Nigeria",
	state: "",
	city: "",
	identityTypeOne: "",
	identityNumberOne: "",
	identityExpiryDateOne: null,
	enterIdentityTypeOne: "",
	identityTypeTwo: "",
	identityNumberTwo: "",
	identityExpiryDateTwo: null,
	enterIdentityTypeTwo: "",
	passportFile: "",
	passportUrl: "",
	passportFilename: "",
	signatureFile: "",
	signatureUrl: "",
	signatureFilename: "",
	identificationFile: "",
	identificationUrl: "",
	identificationFilename: "",
	otherDocumentsFile: "",
	otherDocumentsUrl: "",
	otherDocumentsFilename: "",
	passportFileTwo: "",
	passportUrlTwo: "",
	passportFilenameTwo: "",
	signatureFileTwo: "",
	signatureUrlTwo: "",
	signatureFilenameTwo: "",
	identificationFileTwo: "",
	identificationUrlTwo: "",
	identificationFilenameTwo: "",
};

const KycForm = () => {
	const [accountType, setAccountType] = useState("");
	const [individualFormFields, setIndividualFormFields] = useState(
		defaultIndividualFields
	);
	const [corporateFormFields, setCorporateFormFields] = useState(
		defaultCorporateFields
	);
	const [countryId, setCountryId] = useState("");
	const [stateId, setStateId] = useState("");
	const [individualSubmitLoading, setIndividualSubmitLoading] = useState(false);
	const [corporateSubmitLoading, setCorporateSubmitLoading] = useState(false);

	const {
		data: countriesData = [],
		// isLoading: countriesLoading,
		isError: fetchCountriesError,
		error: fetchCountriesErrorMessage,
	} = useQuery(["get-country"], async () => getCountry(), {
		select: (data) => data,
		staleTime: Infinity,
	});

	const {
		data: statesData = [],
		// isLoading: statesLoading,
		isError: fetchStatesError,
		error: fetchStatesErrorMessage,
	} = useQuery(["get-state", countryId], async () => getState(countryId), {
		select: (data) => data,
		staleTime: Infinity,
		enabled: Boolean(countryId),
	});

	const {
		data: citiesData = [],
		// isLoading: CityLoading,
		isError: fetchCitiesError,
		error: fetchCitiesErrorMessage,
	} = useQuery(["get-city", stateId], async () => getCity(stateId), {
		select: (data) => data.sort((a, b) => a.name.localeCompare(b.name)),
		staleTime: Infinity,
		enabled: Boolean(stateId),
	});

	useEffect(() => {
		if (fetchCountriesError) {
			if (fetchCountriesErrorMessage.response) {
				toast.error(fetchCountriesErrorMessage.response.data?.message);
			} else if (fetchCountriesErrorMessage.request) {
				toast.error("Internal Server Error");
			} else {
				toast.error("Error", fetchCountriesErrorMessage.message);
			}
		}
		if (fetchStatesError) {
			if (fetchStatesErrorMessage.response) {
				toast.error(fetchStatesErrorMessage.response.data?.message);
			} else if (fetchStatesErrorMessage.request) {
				toast.error("Internal Server Error");
			} else {
				toast.error("Error", fetchStatesErrorMessage.message);
			}
		}
		if (fetchCitiesError) {
			if (fetchCitiesErrorMessage.response) {
				toast.error(fetchCitiesErrorMessage.response.data?.message);
			} else if (fetchCitiesErrorMessage.request) {
				toast.error("Internal Server Error");
			} else {
				toast.error("Error", fetchCitiesErrorMessage.message);
			}
		}
	}, [
		fetchCountriesError,
		fetchCountriesErrorMessage,
		fetchStatesError,
		fetchStatesErrorMessage,
		fetchCitiesError,
		fetchCitiesErrorMessage,
	]);

	useEffect(() => {
		if (countriesData.length && accountType === CONSTANTSSTRINGS.INDIVIDUAL) {
			const selectedCountry = countriesData.find(
				(item) =>
					item?.name?.toLowerCase() ===
					individualFormFields?.country?.toLowerCase()
			);
			setCountryId(selectedCountry?.ID);
		}
	}, [countriesData, individualFormFields.country, accountType]);

	useEffect(() => {
		if (
			statesData.length &&
			individualFormFields.state &&
			accountType === CONSTANTSSTRINGS.INDIVIDUAL
		) {
			const selectedState = statesData.find(
				(item) =>
					item?.name?.toLowerCase() ===
					individualFormFields?.state?.toLowerCase()
			);
			setStateId(selectedState?.ID);
		}
	}, [statesData, individualFormFields.state, accountType]);

	useEffect(() => {
		if (countriesData.length && accountType === CONSTANTSSTRINGS.CORPORATE) {
			const selectedCountry = countriesData.find(
				(item) =>
					item?.name?.toLowerCase() ===
					corporateFormFields?.country?.toLowerCase()
			);
			setCountryId(selectedCountry?.ID);
		}
	}, [countriesData, corporateFormFields.country, accountType]);

	useEffect(() => {
		if (
			statesData.length &&
			corporateFormFields.state &&
			accountType === CONSTANTSSTRINGS.CORPORATE
		) {
			const selectedState = statesData.find(
				(item) =>
					item?.name?.toLowerCase() ===
					corporateFormFields?.state?.toLowerCase()
			);
			setStateId(selectedState?.ID);
		}
	}, [statesData, corporateFormFields.state, accountType]);

	const handleIndividualSubmit = async (e) => {
		e.preventDefault();
		if (!individualFormFields.firstname.trim()) {
			toast.error("First name is required");
			return;
		}
		if (!individualFormFields.lastname.trim()) {
			toast.error("Last name is required");
			return;
		}
		if (!individualFormFields.email.trim()) {
			toast.error("Email is required");
			return;
		}
		if (!emailRegex.test(individualFormFields.email.trim())) {
			toast.error("Email is invalid");
			return;
		}
		if (!individualFormFields.gender.trim()) {
			toast.error("Gender is required");
			return;
		}
		if (!individualFormFields.phone.trim()) {
			toast.error("Phone number is required");
			return;
		}
		if (individualFormFields.phone.trim()?.length < 10) {
			toast.error("Phone number is invalid");
			return;
		}
		if (!individualFormFields.addressOne.trim()) {
			toast.error("Address Line 1 is required");
			return;
		}
		if (!individualFormFields.country) {
			toast.error("Country is required");
			return;
		}
		if (!individualFormFields.state) {
			toast.error("State is required");
			return;
		}
		if (!individualFormFields.city) {
			toast.error("City is required");
			return;
		}
		if (!individualFormFields.identityType) {
			toast.error("Identity Type is required");
			return;
		}
		if (
			individualFormFields.identityType === "Others" &&
			!individualFormFields.enterIdentityType
		) {
			toast.error("Please enter identity type");
			return;
		}
		if (!individualFormFields.identityNumber) {
			toast.error("Identity Number is required");
			return;
		}
		if (!individualFormFields.passportFile) {
			toast.error("Passport is required");
			return;
		}
		if (!individualFormFields.signatureFile) {
			toast.error("Signature is required");
			return;
		}
		if (!individualFormFields.identificationFile) {
			toast.error("Identification Document is required");
			return;
		}
		try {
			setIndividualSubmitLoading(true);

			const passportFormData = new FormData();
			// passportFormData.append("CustID", ixtracDetails.OldAccountNumber);
			passportFormData.append("files", individualFormFields.passportFile);

			const signatureFormData = new FormData();
			// signatureFormData.append("CustID", ixtracDetails.OldAccountNumber);
			signatureFormData.append("files", individualFormFields.signatureFile);

			const identificationFormData = new FormData();
			// identificationFormData.append("CustID", ixtracDetails.OldAccountNumber);
			identificationFormData.append(
				"files",
				individualFormFields.identificationFile
			);

			const formData = new FormData();
			// formData.append("CustID", ixtracDetails.OldAccountNumber);
			formData.append("image", individualFormFields.otherDocumentsFile);

			const [
				passportResult,
				signatureResult,
				identificationResult,
				otherDocumentsResult,
			] = individualFormFields.otherDocumentsFile
				? await Promise.all([
						getDocUrl(passportFormData),
						getDocUrl(signatureFormData),
						getDocUrl(identificationFormData),
						getDocUrl(formData),
				  ])
				: await Promise.all([
						getDocUrl(passportFormData),
						getDocUrl(signatureFormData),
						getDocUrl(identificationFormData),
				  ]);

			const payload = {
				firstname: individualFormFields.firstname,
				lastname: individualFormFields.lastname,
				middlename: individualFormFields.middlename,
				title: individualFormFields.title,
				gender: individualFormFields.gender === "male" ? "M" : "F",
				dateOfBirth: individualFormFields.dateOfBirth,
				email: individualFormFields.email,
				phone: individualFormFields.phone,
				secondaryPhone: individualFormFields.secondaryPhone,
				chn: individualFormFields.chn,
				previousAddress: individualFormFields.previousAddress,
				addressLineOne: individualFormFields.addressOne,
				addressLineTwo: individualFormFields.addressTwo,
				country: individualFormFields.country,
				state: individualFormFields.state,
				city: individualFormFields.city,
				nationality: individualFormFields.nationality,
				residenceStateID: stateIdMap[individualFormFields.state],
				identityTypeOne: individualFormFields.identityType,
				identityNumberOne: individualFormFields.identityNumber,
				identityExpiryDateOne: individualFormFields.identityExpiryDate,
				occupation: individualFormFields.occupation,
				employer: individualFormFields.employer,
				employerAddress: individualFormFields.employerAddress,
				enterIdentityTypeOne: individualFormFields.enterIdentityType,
				passportOne: passportResult.data[0] || "",
				signatureOne: signatureResult.data[0] || "",
				identificationOne: identificationResult.data[0] || "",
				otherDocuments: otherDocumentsResult?.data[0] || "",
				accountType,
				kycType: "submission",
			};
			await kycSubmission(payload);
			setIndividualFormFields(defaultIndividualFields);
			toast.success("KYC Update Submission Successful");
			setIndividualSubmitLoading(false);
		} catch (error) {
			setIndividualSubmitLoading(false);
			if (error.response) {
				toast.error(error.response.data.message);
			} else if (error.request) {
				toast.error("Internal Server Error");
			} else {
				toast.error("Error", error.message);
			}
		}
	};

	// corporate form submissiom
	const handleCorporateSubmit = async (e) => {
		e.preventDefault();
		if (!corporateFormFields.companyName.trim()) {
			toast.error("Company Name is required");
			return;
		}
		if (!corporateFormFields.email.trim()) {
			toast.error("Email is required");
			return;
		}
		if (!emailRegex.test(corporateFormFields.email.trim())) {
			toast.error("Email is invalid");
			return;
		}
		if (!corporateFormFields.phone.trim()) {
			toast.error("Phone number is required");
			return;
		}
		if (corporateFormFields.phone.trim()?.length < 10) {
			toast.error("Phone number is invalid");
			return;
		}
		if (!corporateFormFields.addressOne.trim()) {
			toast.error("Address Line 1 is required");
			return;
		}
		if (!corporateFormFields.country) {
			toast.error(" Country is required");
			return;
		}
		if (!corporateFormFields.state) {
			toast.error("State is required");
			return;
		}
		if (!corporateFormFields.city) {
			toast.error("City is required");
			return;
		}
		if (!corporateFormFields.identityTypeOne) {
			toast.error("Identity Type 1 is required");
			return;
		}
		if (
			corporateFormFields.identityTypeOne === "Others" &&
			!corporateFormFields.enterIdentityTypeOne
		) {
			toast.error("Please enter identity type 1");
			return;
		}
		if (
			corporateFormFields.identityTypeTwo === "Others" &&
			!corporateFormFields.enterIdentityTypeTwo
		) {
			toast.error("Please enter identity type 2");
			return;
		}
		if (!corporateFormFields.identityNumberOne) {
			toast.error("Identity Number 1 is required");
			return;
		}
		if (!corporateFormFields.identityTypeOne) {
			toast.error("Identity Type 1 is required");
			return;
		}
		if (!corporateFormFields.passportFile) {
			toast.error("Passport 1 is required");
			return;
		}
		if (!corporateFormFields.signatureFile) {
			toast.error("Signature 1 is required");
			return;
		}
		if (!corporateFormFields.identificationFile) {
			toast.error("Identification Document 1 is required");
			return;
		}
		try {
			setCorporateSubmitLoading(true);

			const passportFormData = new FormData();
			passportFormData.append("files", corporateFormFields.passportFile);

			const signatureFormData = new FormData();
			signatureFormData.append("files", corporateFormFields.signatureFile);

			const identificationFormData = new FormData();
			identificationFormData.append(
				"files",
				corporateFormFields.identificationFile
			);

			const formData = new FormData();
			formData.append("image", corporateFormFields.otherDocumentsFile);

			const passportFormDataTwo = new FormData();
			passportFormDataTwo.append("files", corporateFormFields.passportFileTwo);

			const signatureFormDataTwo = new FormData();
			signatureFormDataTwo.append(
				"files",
				corporateFormFields.signatureFileTwo
			);

			const identificationFormDataTwo = new FormData();
			identificationFormDataTwo.append(
				"files",
				corporateFormFields.identificationFileTwo
			);

			const [
				passportResult,
				signatureResult,
				identificationResult,
				otherDocumentsResult,
			] = corporateFormFields.otherDocumentsFile
				? await Promise.all([
						getDocUrl(passportFormData),
						getDocUrl(signatureFormData),
						getDocUrl(identificationFormData),
						getDocUrl(formData),
				  ])
				: await Promise.all([
						getDocUrl(passportFormData),
						getDocUrl(signatureFormData),
						getDocUrl(identificationFormData),
				  ]);

			let passportResultTwo;
			if (corporateFormFields.passportFileTwo) {
				passportResultTwo = await getDocUrl(passportFormDataTwo);
			}
			let signatureResultTwo;
			if (corporateFormFields.signatureFileTwo) {
				signatureResultTwo = await getDocUrl(signatureFormDataTwo);
			}
			let identificationResultTwo;
			if (corporateFormFields.identificationFileTwo) {
				identificationResultTwo = await getDocUrl(identificationFormDataTwo);
			}

			const payload = {
				lastname: corporateFormFields.companyName,
				email: corporateFormFields.email,
				phone: corporateFormFields.phone,
				secondaryPhone: corporateFormFields.secondaryPhone,
				chn: corporateFormFields.chn,
				previousAddress: corporateFormFields.previousAddress,
				addressLineOne: corporateFormFields.addressOne,
				addressLineTwo: corporateFormFields.addressTwo,
				country: corporateFormFields.country,
				state: corporateFormFields.state,
				city: corporateFormFields.city,
				nationality: corporateFormFields.nationality,
				residenceStateID: stateIdMap[corporateFormFields.state],
				identityTypeOne: corporateFormFields.identityTypeOne,
				identityNumberOne: corporateFormFields.identityNumberOne,
				identityExpiryDateOne: corporateFormFields.identityExpiryDateOne,
				enterIdentityTypeOne: corporateFormFields.enterIdentityType,
				identityTypeTwo: corporateFormFields.identityTypeTwo,
				identityNumberTwo: corporateFormFields.identityNumberTwo,
				identityExpiryDateTwo: corporateFormFields.identityExpiryDateTwo,
				enterIdentityTypeTwo: corporateFormFields.enterIdentityTypeTwo,
				occupation: corporateFormFields.occupation,
				employer: corporateFormFields.employer,
				employerAddress: corporateFormFields.employerAddress,
				passportOne: passportResult.data[0] || "",
				signatureOne: signatureResult.data[0] || "",
				identificationOne: identificationResult.data[0] || "",
				passportTwo: corporateFormFields.passportFileTwo
					? passportResultTwo.data[0]
					: "",
				signatureTwo: corporateFormFields.signatureFileTwo
					? signatureResultTwo.data[0]
					: "",
				identificationTwo: corporateFormFields.identificationFileTwo
					? identificationResultTwo.data[0]
					: "",
				otherDocuments: otherDocumentsResult?.data[0] || "",
				accountType,
				kycType: "submission",
			};
			await kycSubmission(payload);
			setCorporateFormFields(defaultCorporateFields);
			toast.success("KYC Update Submission Successful");
			setCorporateSubmitLoading(false);
		} catch (error) {
			setCorporateSubmitLoading(false);
			if (error.response) {
				toast.error(error.response.data.message);
			} else if (error.request) {
				toast.error("Internal Server Error");
			} else {
				toast.error("Error", error.message);
			}
		}
	};

	return (
		<div>
			<CssBaseline />
			<FormNavBar />
			<DrawerHeader />
			<DrawerHeader />
			<Box
				sx={{ minHeight: "100vh", backgroundColor: "#f7fafc", width: "100vw" }}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Box
						sx={{
							padding: { md: "2rem", xs: "1rem" },
							width: "43rem",
						}}
					>
						<Box sx={{ marginBottom: "3rem" }}>
							<FieldHeader
								header="Account Type"
								subHeader="Choose account type"
							/>
							<Box
								sx={{
									backgroundColor: "#fff",
									padding: "1.5rem",
								}}
							>
								<Grid container spacing={2}>
									<GridItem>
										<SelectFieldComponent
											width={selectFieldWidth}
											values={ACCOUNTTYPES}
											value={accountType}
											onChange={(e) => setAccountType(e.target.value)}
											label="Account Type"
											id="accountType"
										/>
									</GridItem>
								</Grid>
							</Box>
						</Box>
						{accountType === CONSTANTSSTRINGS.INDIVIDUAL && (
							<IndividualForm
								individualFormFields={individualFormFields}
								setIndividualFormFields={setIndividualFormFields}
								countries={countriesData}
								countryStates={statesData}
								cities={citiesData}
								handleSubmit={handleIndividualSubmit}
								loading={individualSubmitLoading}
							/>
						)}
						{accountType === CONSTANTSSTRINGS.CORPORATE && (
							<CorporateForm
								corporateFormFields={corporateFormFields}
								setCorporateFormFields={setCorporateFormFields}
								countries={countriesData}
								countryStates={statesData}
								cities={citiesData}
								handleSubmit={handleCorporateSubmit}
								loading={corporateSubmitLoading}
							/>
						)}
					</Box>
				</Box>
			</Box>
		</div>
	);
};

export default KycForm;
